/**
 * Customer API — Secure wrappers for customer edge functions
 * 
 * All PII-bearing customer operations route through edge functions
 * to ensure encryption/decryption happens server-side.
 */

import { supabase } from '@/integrations/supabase/client';
import type { Customer } from '@/lib/customers/types';

interface CustomerListItem {
  id: string;
  full_name: string;
  phone_masked: string;
  email_masked: string;
  created_at: string;
  vehicles: { count: number } | unknown;
}

interface FetchCustomerOptions {
  customer_id: string;
  shop_id: string;
}

interface CreateCustomerInput {
  shop_id: string;
  full_name: string;
  phone?: string | null;
  email?: string | null;
  address?: Record<string, unknown> | null;
  notes?: string | null;
  sms_opt_in?: boolean;
  email_opt_in?: boolean;
  preferred_channel?: 'sms' | 'email' | 'phone' | null;
  fallback_channel?: 'sms' | 'email' | 'phone' | null;
}

interface UpdateCustomerInput extends Partial<Omit<CreateCustomerInput, 'shop_id'>> {
  customer_id: string;
  shop_id: string;
}

interface ListCustomersOptions {
  shop_id: string;
  limit?: number;
  offset?: number;
  order_by?: string;
  ascending?: boolean;
}

/**
 * Fetch a single customer with decrypted PII fields.
 * Uses customer-read edge function.
 */
export async function fetchCustomer({ customer_id, shop_id }: FetchCustomerOptions): Promise<Customer | null> {
  const { data, error } = await supabase.functions.invoke('customer-read', {
    body: { customer_id, shop_id },
  });

  if (error) throw new Error(error.message || 'Failed to fetch customer');
  if (data?.error) throw new Error(data.error);
  return data?.customer || null;
}

/**
 * Create a new customer via customer-write edge function.
 * Returns the new customer ID.
 */
export async function createCustomer(input: CreateCustomerInput): Promise<string> {
  const { data, error } = await supabase.functions.invoke('customer-write', {
    body: input,
  });

  if (error) throw new Error(error.message || 'Failed to create customer');
  if (data?.error) throw new Error(data.error);
  return data?.customer_id;
}

/**
 * Update an existing customer via customer-write edge function.
 */
export async function updateCustomer(input: UpdateCustomerInput): Promise<string> {
  const { data, error } = await supabase.functions.invoke('customer-write', {
    body: input,
  });

  if (error) throw new Error(error.message || 'Failed to update customer');
  if (data?.error) throw new Error(data.error);
  return data?.customer_id;
}

/**
 * List customers with masked PII for list views.
 * Uses customer-list edge function.
 */
export async function listCustomers(options: ListCustomersOptions): Promise<CustomerListItem[]> {
  const { data, error } = await supabase.functions.invoke('customer-list', {
    body: options,
  });

  if (error) throw new Error(error.message || 'Failed to list customers');
  if (data?.error) throw new Error(data.error);
  return data?.customers || [];
}
